// SoundManager.ts — Procedural Web Audio sound effects and ambient music for Ultimaze Tower Wars

export class SoundManager {
  private ctx: AudioContext | null = null
  enabled = true
  private volume = 0.4

  // ── Background Music ─────────────────────────────────────────────────────────

  private _musicGain: GainNode | null = null
  private _musicNodes: AudioNode[] = []
  private _arpeggioTimer: ReturnType<typeof setInterval> | null = null
  private _musicMuted = false
  private _musicRunning = false

  /** Call on first user gesture (pointerdown) to satisfy browser autoplay policy. */
  init(): void {
    if (this.ctx !== null) return
    this.ctx = new AudioContext()
  }

  setEnabled(on: boolean): void {
    this.enabled = on
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
  }

  // ── Public sound methods ─────────────────────────────────────────────────

  playTowerPlace(): void {
    this.playTone(220, 'sine', 0.08)
  }

  playTowerUpgrade(): void {
    this.playFreqSweep(330, 660, 'sine', 0.15)
  }

  playUnitSent(): void {
    this.playFreqSweep(150, 80, 'sine', 0.12)
  }

  playUnitDied(): void {
    this.playTone(100, 'triangle', 0.08)
  }

  playUnitReachedBase(): void {
    // Short ascending victory chime: G4 → B4 → D5 (G-major arpeggio, sine)
    const notes = [392.0, 493.88, 587.33]
    notes.forEach((f, i) => {
      this.scheduleTone(f, 'sine', 0.12, i * 0.08, this.volume * 0.55)
    })
  }

  playMatchWin(): void {
    // C4-E4-G4-C5 ascending
    const freqs = [261.63, 329.63, 392.0, 523.25]
    freqs.forEach((f, i) => { this.scheduleTone(f, 'sine', 0.12, i * 0.12, this.volume) })
  }

  playMatchLose(): void {
    // C4-A3-F3-D3 descending
    const freqs = [261.63, 220.0, 174.61, 146.83]
    freqs.forEach((f, i) => { this.scheduleTone(f, 'sine', 0.12, i * 0.12, this.volume) })
  }

  playGoldTick(): void {
    this.playTone(1000, 'sine', 0.03, this.volume * 0.1)
  }

  playSpellCast(): void {
    this.playFreqSweep(200, 800, 'sine', 0.2)
  }

  // ── Background Music — Public API ────────────────────────────────────────────

  /**
   * Start procedural ambient music for a match.
   * @param element - the dominant element for the current map/match (sets the harmonic theme)
   */
  startMatchMusic(element: string = 'neutral'): void {
    if (!this.ctx || this._musicRunning) return

    // Respect prefers-reduced-motion: use a low-volume static pad only
    const reducedMotion = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this._musicRunning = true

    this._musicGain = this.ctx.createGain()
    this._musicGain.gain.setValueAtTime(0, this.ctx.currentTime)
    this._musicGain.gain.linearRampToValueAtTime(
      this._musicMuted ? 0 : 0.15,
      this.ctx.currentTime + 3
    )
    this._musicGain.connect(this.ctx.destination)

    // Base frequencies per element theme (minor/modal flavour for dark fantasy)
    const themes: Record<string, number[]> = {
      fire:    [110, 138.6, 165, 220],
      water:   [98, 130.8, 164.8, 196],
      nature:  [87.3, 110, 146.8, 174.6],
      elektro: [123.5, 155.6, 185, 246.9],
      light:   [130.8, 164.8, 196, 261.6],
      shadow:  [92.5, 110, 138.6, 184.9],
      neutral: [110, 130.8, 165, 196],
    }
    const freqs = themes[element] ?? themes['neutral']!

    // Pad layer: soft detuned oscillators for ambient warmth
    freqs.forEach((freq, i) => {
      if (!this.ctx || !this._musicGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = i % 2 === 0 ? 'sine' : 'triangle'
      osc.frequency.value = freq
      osc.detune.value = (i - 1) * 7  // slight detuning for warmth
      gain.gain.value = 0.06 / freqs.length
      osc.connect(gain)
      gain.connect(this._musicGain)
      osc.start()
      this._musicNodes.push(osc, gain)
    })

    // Skip arpeggio layer entirely when prefers-reduced-motion is active
    if (!reducedMotion) {
      const arpGain = this.ctx.createGain()
      arpGain.gain.value = this._musicMuted ? 0 : 0.08
      arpGain.connect(this.ctx.destination)
      this._musicNodes.push(arpGain)

      let arpIdx = 0
      this._arpeggioTimer = setInterval(() => {
        if (!this.ctx || this._musicMuted || !this._musicRunning) return
        const freq = freqs[arpIdx % freqs.length]! * 2  // octave up
        const osc = this.ctx.createOscillator()
        const env = this.ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        env.gain.setValueAtTime(0.04, this.ctx.currentTime)
        env.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 1.2)
        osc.connect(env)
        env.connect(arpGain)
        osc.start()
        osc.stop(this.ctx.currentTime + 1.3)
        arpIdx++
      }, 800)
    }
  }

  /** Fade out and tear down all music nodes. Safe to call when music is not running. */
  stopMatchMusic(): void {
    if (!this._musicRunning) return
    this._musicRunning = false
    if (this._arpeggioTimer !== null) {
      clearInterval(this._arpeggioTimer)
      this._arpeggioTimer = null
    }
    if (this._musicGain && this.ctx) {
      this._musicGain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 1.5)
      setTimeout(() => {
        this._musicNodes.forEach((n) => {
          try { (n as OscillatorNode).stop?.() } catch { /* node already stopped */ }
        })
        this._musicNodes = []
        this._musicGain = null
      }, 1600)
    }
  }

  /**
   * Toggle music mute state.
   * @returns the new muted state (true = muted)
   */
  toggleMute(): boolean {
    this._musicMuted = !this._musicMuted
    if (this._musicGain && this.ctx) {
      this._musicGain.gain.linearRampToValueAtTime(
        this._musicMuted ? 0 : 0.15,
        this.ctx.currentTime + 0.3
      )
    }
    return this._musicMuted
  }

  /** Returns true when music is currently muted. */
  isMuted(): boolean { return this._musicMuted }

  // ── Private helpers ──────────────────────────────────────────────────────

  private playTone(
    freq: number,
    type: OscillatorType,
    duration: number,
    overrideVolume?: number
  ): void {
    this.scheduleTone(freq, type, duration, 0, overrideVolume)
  }

  private playFreqSweep(
    startFreq: number,
    endFreq: number,
    type: OscillatorType,
    duration: number
  ): void {
    if (!this.enabled || this.ctx === null) return
    const ctx = this.ctx
    const now = ctx.currentTime

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = type
    osc.frequency.setValueAtTime(startFreq, now)
    osc.frequency.linearRampToValueAtTime(endFreq, now + duration)

    gain.gain.setValueAtTime(this.volume, now)
    gain.gain.linearRampToValueAtTime(0, now + duration)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + duration)
  }

  private scheduleTone(
    freq: number,
    type: OscillatorType,
    duration: number,
    delaySeconds: number,
    overrideVolume?: number
  ): void {
    if (!this.enabled || this.ctx === null) return
    const ctx = this.ctx
    const vol = overrideVolume ?? this.volume
    const startTime = ctx.currentTime + delaySeconds

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = type
    osc.frequency.setValueAtTime(freq, startTime)

    gain.gain.setValueAtTime(vol, startTime)
    gain.gain.linearRampToValueAtTime(0, startTime + duration)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(startTime)
    osc.stop(startTime + duration)
  }
}

export const soundManager = new SoundManager()

// Expose on window so HUD (React) and external code can reach the singleton
// without creating a circular import between @game/ and @ui/ layers.
;(window as unknown as Record<string, unknown>).__soundManager = soundManager
